/**
 * ProspectsAdmin — admin page for managing public-signup applications
 *
 * Mirrors UsersAdmin layout (tabs, table, detail panel, modals) but works
 * against the `prospects` table populated by signup.madun.eu /
 * signup.wingmentor.net.
 *
 * Actions:
 *   - Approve   → POST /api/prospects/approve (creates airline if needed,
 *                 sends Supabase Auth invite + Resend email, marks status
 *                 onboarded)
 *   - Decline   → POST /api/prospects/decline (status=declined + reason)
 *   - Add note  → direct supabase update (admin_notes only, no status change)
 *
 * Permissions: super-admin only (Global airline + role='admin').
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { GLOBAL_AIRLINE_ID } from '../../lib/constants';
import { useAirline } from '../../contexts/AirlineContext';
import { useDevice } from '../../contexts/DeviceContext';
import {
  Check,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Mail,
  Calendar,
  Building,
  Search,
  Send,
  ChevronRight,
} from 'lucide-react';

// =====================================================================
// Types
// =====================================================================

type ProspectStatus = 'new' | 'contacted' | 'approved' | 'declined' | 'onboarded';
type TabType = ProspectStatus | 'all';

interface Prospect {
  id: string;
  full_name: string;
  email: string;
  airline_name: string;
  function_role: 'instructor' | 'trainee' | 'admin';
  airline_role: 'admin' | 'editor' | 'viewer';
  mobile_platform: 'ios' | 'android' | 'both' | null;
  apple_id_email: string | null;
  google_account_email: string | null;
  device_model: string | null;
  device_os: string | null;
  type_rating: string | null;
  instructor_category: string | null;
  cockpit_language: string | null;
  message: string | null;
  // GDPR audit (ROPA-WIN-001 § H) — see migration 197_prospects_gdpr_audit_fields.sql
  notice_acknowledged_at: string;
  privacy_notice_version: string;
  notice_text_hash: string | null;
  marketing_consent: boolean;
  marketing_consent_at: string | null;
  marketing_consent_text_version: string | null;
  marketing_consent_withdrawn_at: string | null;
  status: ProspectStatus;
  admin_notes: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  converted_to_airline_id: string | null;
  converted_to_user_id: string | null;
  converted_at: string | null;
  created_at: string;
  updated_at: string;
}

const TABS: Array<{ key: TabType; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'onboarded', label: 'Onboarded' },
  { key: 'declined', label: 'Declined' },
  { key: 'all', label: 'All' },
];

// =====================================================================
// Main component
// =====================================================================

interface ProspectsAdminProps {
  /**
   * Optional override for the "New" sub-tab badge count. When the parent
   * (Admin.tsx) already maintains a real-time subscription for status='new',
   * pass that here so the badge stays accurate even when the user is viewing
   * a different sub-tab (e.g. "Contacted") whose filtered query wouldn't
   * include any 'new' rows.
   */
  newCount?: number;
}

export default function ProspectsAdmin({ newCount: newCountProp }: ProspectsAdminProps = {}) {
  const { isSuperAdmin } = useAirline();
  const { isDesktop } = useDevice();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabType>('new');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Prospect | null>(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showDecline, setShowDecline] = useState(false);

  // ── Data ────────────────────────────────────────────────────────────
  const {
    data: prospects,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-prospects', activeTab, search],
    queryFn: async (): Promise<Prospect[]> => {
      let query = supabase
        .from('prospects')
        .select('*')
        .order('created_at', { ascending: false });
      if (activeTab !== 'all') query = query.eq('status', activeTab);
      if (search.trim()) {
        const pattern = `%${search.trim()}%`;
        query = query.or(
          `full_name.ilike.${pattern},email.ilike.${pattern},airline_name.ilike.${pattern}`,
        );
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Prospect[];
    },
    enabled: isSuperAdmin,
  });

  // Prefer the parent-supplied count (real-time, status-tab agnostic). Fall
  // back to deriving it from the current page's rows so the component still
  // shows a sensible badge when used standalone.
  const newCount = newCountProp ?? (prospects?.filter((p) => p.status === 'new').length ?? 0);

  // ── Mutations ───────────────────────────────────────────────────────
  const approveMutation = useMutation({
    mutationFn: async (args: { prospect: Prospect; payload: ApprovePayload }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated.');
      const res = await fetch('/api/prospects/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prospect_id: args.prospect.id,
          ...args.payload,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as {
        user_id: string;
        airline_id: string;
        airline_ids: string[];
        airline_created: boolean;
        invite_link: string;
        email_sent: boolean;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prospects'] });
      setShowApprove(false);
      setSelected(null);
    },
  });

  const declineMutation = useMutation({
    mutationFn: async (args: { prospect_id: string; reason: string }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated.');
      const res = await fetch('/api/prospects/decline', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(args),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prospects'] });
      setShowDecline(false);
      setSelected(null);
    },
  });

  const noteMutation = useMutation({
    mutationFn: async (args: { prospect_id: string; admin_notes: string; status?: ProspectStatus }) => {
      const now = new Date().toISOString();
      const update: Record<string, unknown> = { admin_notes: args.admin_notes };
      if (args.status) {
        update.status = args.status;
        update.reviewed_at = now;
        // reviewed_by must be set when status leaves 'new' per the trigger;
        // the trigger uses auth.uid() implicitly via RLS but we set it
        // explicitly for clarity / older RLS contexts.
        const {
          data: { user },
        } = await supabase.auth.getUser();
        update.reviewed_by = user?.id;
      }
      const { error } = await supabase
        .from('prospects')
        .update(update)
        .eq('id', args.prospect_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prospects'] });
    },
  });

  // ── Guard ───────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You need super admin permissions to access this page.
          </p>
        </div>
      </div>
    );
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  const formatDate = (s: string | null) =>
    s
      ? new Date(s).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '-';

  const statusBadge = (status: ProspectStatus) => {
    const base = 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'new':
        return (
          <span className={`${base} bg-amber-100 text-amber-800`}>
            <Clock size={12} /> New
          </span>
        );
      case 'contacted':
        return (
          <span className={`${base} bg-sky-100 text-sky-800`}>
            <Mail size={12} /> Contacted
          </span>
        );
      case 'approved':
        return (
          <span className={`${base} bg-emerald-100 text-emerald-800`}>
            <CheckCircle2 size={12} /> Approved
          </span>
        );
      case 'onboarded':
        return (
          <span className={`${base} bg-green-100 text-green-800`}>
            <Check size={12} /> Onboarded
          </span>
        );
      case 'declined':
        return (
          <span className={`${base} bg-red-100 text-red-800`}>
            <XCircle size={12} /> Declined
          </span>
        );
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div>
      {/* Tabs — mobile: vertical list; desktop: horizontal bar */}
      <div className="md:hidden bg-white rounded-xl shadow-sm border border-steel/20 overflow-hidden divide-y divide-steel/10 mb-4">
        {TABS.map((t) => {
          const icon =
            t.key === 'new'       ? <Clock size={18} /> :
            t.key === 'contacted' ? <Mail size={18} /> :
            t.key === 'onboarded' ? <CheckCircle2 size={18} /> :
            t.key === 'declined'  ? <XCircle size={18} /> :
                                    <Building size={18} />;
          const badge = t.key === 'new' && newCount > 0 ? newCount : null;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`relative w-full flex items-center gap-4 px-4 py-3.5 transition ${
                activeTab === t.key ? 'bg-orca-elevate/5' : 'hover:bg-ice'
              }`}
            >
              <span className={`absolute left-0 inset-y-0 w-1 rounded-r transition-colors ${activeTab === t.key ? 'bg-orca-elevate' : 'bg-transparent'}`} />
              <span className={activeTab === t.key ? 'text-orca-elevate' : 'text-steel/60'}>{icon}</span>
              <span className={`flex-1 text-left font-semibold text-sm ${activeTab === t.key ? 'text-orca-elevate' : 'text-navy'}`}>{t.label}</span>
              {badge && <span className="bg-signal text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>}
              <ChevronRight size={16} className="text-steel/40 shrink-0" />
            </button>
          );
        })}
      </div>

      <div className="hidden md:block border-b border-steel/20 mb-4">
        <nav className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`relative px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.key
                  ? 'border-orca-elevate text-orca-elevate'
                  : 'border-transparent text-steel hover:text-orca-command'
              }`}
            >
              {t.label}
              {t.key === 'new' && newCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-signal text-white text-xs">
                  {newCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or airline…"
          className="w-full pl-9 pr-3 py-2 border border-steel/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orca-elevate/30"
        />
      </div>

      {/* Loading / error / empty */}
      {isLoading && (
        <div className="flex items-center gap-2 text-steel py-6">
          <Loader2 size={16} className="animate-spin" /> Loading prospects…
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-red-600 py-6">
          <AlertCircle size={16} /> {String((error as Error).message)}
        </div>
      )}
      {!isLoading && !error && prospects && prospects.length === 0 && (
        <div className="text-center text-steel py-12">No prospects in this view.</div>
      )}

      {/* Table / Cards */}
      {prospects && prospects.length > 0 && (
        <>
          {/* Mobile: full-screen detail overlay — only mounted when not desktop */}
          {selected && !isDesktop && (
            <div className="fixed inset-0 z-40 bg-white">
              <ProspectDetail
                mobile
                prospect={selected}
                onClose={() => setSelected(null)}
                onApprove={() => setShowApprove(true)}
                onDecline={() => setShowDecline(true)}
                onMarkContacted={() =>
                  noteMutation.mutate({
                    prospect_id: selected.id,
                    admin_notes: selected.admin_notes ?? '',
                    status: 'contacted',
                  })
                }
                onSaveNotes={(notes) =>
                  noteMutation.mutate({
                    prospect_id: selected.id,
                    admin_notes: notes,
                  })
                }
                noteMutationPending={noteMutation.isPending}
              />
            </div>
          )}

          {/* Mobile cards — outside the grid so they match the full width of tabs/search above */}
          <div className="md:hidden space-y-3">
            {prospects.map((p) => (
              <div
                key={p.id}
                onClick={() => setSelected(p)}
                className="bg-white rounded-xl border border-steel/20 shadow-sm overflow-hidden cursor-pointer active:bg-ice/50"
              >
                <div className="flex items-center gap-3 px-4 pt-4 pb-2">
                  <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
                    <span className="text-orca-elevate font-semibold text-sm">
                      {p.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col items-start gap-0.5">
                    <p className="font-semibold text-sm text-navy truncate w-full">{p.full_name}</p>
                    {statusBadge(p.status)}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 px-4 pb-3 text-xs text-steel">
                  <span className="flex items-center gap-2">
                    <Mail size={13} className="text-gray-400 shrink-0" />
                    <span className="truncate">{p.email}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Building size={13} className="text-gray-400 shrink-0" />
                    {p.airline_name}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar size={13} className="text-gray-400 shrink-0" />
                    {formatDate(p.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table + optional side panel */}
          <div className={`hidden md:grid gap-4 items-start ${selected ? 'lg:grid-cols-[1fr_400px]' : ''}`}>
            <div className="bg-white rounded-lg border border-steel/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ice text-xs uppercase text-steel">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Airline</th>
                    <th className="text-left px-3 py-2">Function</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => setSelected(p)}
                      className={`cursor-pointer border-t border-steel/10 hover:bg-ice/50 ${
                        selected?.id === p.id ? 'bg-ice' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.full_name}</div>
                        <div className="text-xs text-steel">{p.email}</div>
                      </td>
                      <td className="px-3 py-2">{p.airline_name}</td>
                      <td className="px-3 py-2 capitalize">{p.function_role}</td>
                      <td className="px-3 py-2">{statusBadge(p.status)}</td>
                      <td className="px-3 py-2 text-xs text-steel">{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Desktop side panel — only mounted when isDesktop, no duplicate DOM */}
            {selected && isDesktop && (
              <ProspectDetail
                prospect={selected}
                onClose={() => setSelected(null)}
                onApprove={() => setShowApprove(true)}
                onDecline={() => setShowDecline(true)}
                onMarkContacted={() =>
                  noteMutation.mutate({
                    prospect_id: selected.id,
                    admin_notes: selected.admin_notes ?? '',
                    status: 'contacted',
                  })
                }
                onSaveNotes={(notes) =>
                  noteMutation.mutate({
                    prospect_id: selected.id,
                    admin_notes: notes,
                  })
                }
                noteMutationPending={noteMutation.isPending}
              />
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {showApprove && selected && (
        <ApproveModal
          prospect={selected}
          onClose={() => setShowApprove(false)}
          onConfirm={(payload) => approveMutation.mutate({ prospect: selected, payload })}
          isPending={approveMutation.isPending}
          error={approveMutation.error ? String((approveMutation.error as Error).message) : null}
        />
      )}
      {showDecline && selected && (
        <DeclineModal
          prospect={selected}
          onClose={() => setShowDecline(false)}
          onConfirm={(reason) =>
            declineMutation.mutate({ prospect_id: selected.id, reason })
          }
          isPending={declineMutation.isPending}
          error={declineMutation.error ? String((declineMutation.error as Error).message) : null}
        />
      )}
    </div>
  );
}

// =====================================================================
// Detail panel
// =====================================================================

interface DetailProps {
  prospect: Prospect;
  onClose: () => void;
  onApprove: () => void;
  onDecline: () => void;
  onMarkContacted: () => void;
  onSaveNotes: (notes: string) => void;
  noteMutationPending: boolean;
  mobile?: boolean;
}

function ProspectDetail({
  prospect,
  onClose,
  onApprove,
  onDecline,
  onMarkContacted,
  onSaveNotes,
  noteMutationPending,
  mobile = false,
}: DetailProps) {
  const [notes, setNotes] = useState(prospect.admin_notes ?? '');
  const isTerminal = prospect.status === 'onboarded' || prospect.status === 'declined';

  return (
    <div className={mobile
      ? 'absolute inset-0 flex flex-col overflow-hidden bg-white'
      : 'bg-white rounded-lg border border-steel/20 sticky top-4 flex flex-col max-h-[calc(100vh-6rem)] overflow-hidden'
    }>
      {/* Fixed header */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-steel/10 shrink-0">
        <div>
          <h3 className="font-heading font-semibold text-lg">{prospect.full_name}</h3>
          <p className="text-xs text-steel">{prospect.email}</p>
        </div>
        <button onClick={onClose} className="text-steel hover:text-orca-command">
          <X size={18} />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
        <dl className="space-y-2 text-sm">
          <Row label="Airline">
            <span className="inline-flex items-center gap-1">
              <Building size={12} /> {prospect.airline_name}
            </span>
          </Row>
          <Row label="Function">
            <span className="capitalize">{prospect.function_role}</span>
          </Row>
          <Row label="Airline role">
            <span className="capitalize">{prospect.airline_role}</span>
          </Row>
          {prospect.mobile_platform && <Row label="Mobile">{prospect.mobile_platform}</Row>}
          {prospect.apple_id_email && <Row label="Apple ID">{prospect.apple_id_email}</Row>}
          {prospect.google_account_email && (
            <Row label="Google account">{prospect.google_account_email}</Row>
          )}
          {prospect.device_model && (
            <Row label="Device">
              {prospect.device_model}
              {prospect.device_os ? ` · ${prospect.device_os}` : ''}
            </Row>
          )}
          {prospect.type_rating && <Row label="Type rating">{prospect.type_rating}</Row>}
          {prospect.instructor_category && (
            <Row label="Instructor cat.">{prospect.instructor_category}</Row>
          )}
          {prospect.cockpit_language && (
            <Row label="Cockpit lang.">{prospect.cockpit_language}</Row>
          )}
          {prospect.message && (
            <Row label="Message">
              <div className="whitespace-pre-wrap text-sm">{prospect.message}</div>
            </Row>
          )}
          <Row label="Submitted">
            <span className="inline-flex items-center gap-1 text-steel">
              <Calendar size={12} />
              {new Date(prospect.created_at).toLocaleString()}
            </span>
          </Row>
          <Row label="Notice ack">
            <span className="text-sm">
              v{prospect.privacy_notice_version} ·{' '}
              {new Date(prospect.notice_acknowledged_at).toLocaleString()}
            </span>
          </Row>
          {prospect.notice_text_hash && (
            <Row label="Notice hash">
              <span
                className="font-mono text-xs text-steel"
                title={prospect.notice_text_hash}
              >
                {prospect.notice_text_hash.slice(0, 12)}…
              </span>
            </Row>
          )}
          <Row label="Marketing">
            {prospect.marketing_consent ? (
              <span className="text-sm">
                Opted in
                {prospect.marketing_consent_text_version
                  ? ` (v${prospect.marketing_consent_text_version})`
                  : ''}
                {prospect.marketing_consent_at
                  ? ` · ${new Date(prospect.marketing_consent_at).toLocaleString()}`
                  : ''}
                {prospect.marketing_consent_withdrawn_at && (
                  <span className="ml-1 text-signal">
                    · withdrawn{' '}
                    {new Date(prospect.marketing_consent_withdrawn_at).toLocaleString()}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-sm text-steel">No</span>
            )}
          </Row>
        </dl>

        <div className="mt-4">
          <label className="block text-xs font-medium text-steel mb-1">Admin notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 border border-steel/30 rounded text-sm"
            placeholder="Internal notes (not visible to the prospect)"
          />
          <button
            onClick={() => onSaveNotes(notes)}
            disabled={noteMutationPending || notes === (prospect.admin_notes ?? '')}
            className="mt-1 text-xs text-orca-elevate hover:underline disabled:opacity-50"
          >
            Save notes
          </button>
        </div>
      </div>

      {/* Fixed footer — always visible */}
      <div className="flex gap-2 px-4 py-3 border-t border-steel/10 bg-white rounded-b-lg shrink-0">
        {isTerminal ? (
          <p className="flex-1 text-center text-xs text-steel py-1">
            {prospect.status === 'onboarded' ? 'User has been onboarded.' : 'Application was declined.'}
          </p>
        ) : (
          <>
            <button
              onClick={onApprove}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-orca-elevate text-white rounded-md text-sm font-medium hover:bg-orca-elevate/90"
            >
              <Check size={14} /> Approve & invite
            </button>
            {prospect.status === 'new' && (
              <button
                onClick={onMarkContacted}
                className="px-3 py-2 border border-steel/30 rounded-md text-sm hover:bg-ice"
                title="Mark as Contacted without making a decision"
              >
                <Send size={14} />
              </button>
            )}
            <button
              onClick={onDecline}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 border border-red-200 text-red-700 rounded-md text-sm font-medium hover:bg-red-50"
            >
              <X size={14} /> Decline
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <dt className="text-xs uppercase text-steel">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// =====================================================================
// Approve modal
// =====================================================================

interface ApprovePayload {
  airline_ids: string[];
  create_new_airline_name?: string;
  default_airline_id?: string;
  default_is_new?: boolean;
  override_airline_role?: 'admin' | 'editor' | 'viewer';
}

function ApproveModal({
  prospect,
  onClose,
  onConfirm,
  isPending,
  error,
}: {
  prospect: Prospect;
  onClose: () => void;
  onConfirm: (payload: ApprovePayload) => void;
  isPending: boolean;
  error: string | null;
}) {
  const { data: airlines, isLoading: airlinesLoading } = useQuery({
    queryKey: ['admin-airlines-list'],
    queryFn: async () => {
      const { data, error: e } = await supabase
        .from('airlines')
        .select('id, name, code')
        .order('name');
      if (e) throw e;
      return (data ?? []) as { id: string; name: string; code: string }[];
    },
  });

  const [filter, setFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createNew, setCreateNew] = useState(false);
  const [newName, setNewName] = useState(prospect.airline_name);
  const [defaultChoice, setDefaultChoice] = useState<string | null>(null);
  const [override, setOverride] = useState<Prospect['airline_role']>(prospect.airline_role);

  // Hide the Global airline: every approved user already gets a Global (viewer)
  // membership automatically via the signup trigger, so it never needs to be
  // selected here — and selecting it with a non-viewer role would wrongly elevate
  // the prospect on the shared Global docs airline.
  const selectable = (airlines ?? []).filter((a) => a.id !== GLOBAL_AIRLINE_ID);

  // Pre-select an EXISTING airline whose name matches the prospect's entry (helpful,
  // and avoids accidentally creating a duplicate). Never pre-creates a new airline.
  React.useEffect(() => {
    if (!airlines || selectedIds.size > 0 || createNew) return;
    const match = selectable.find(
      (a) => a.name.trim().toLowerCase() === prospect.airline_name.trim().toLowerCase(),
    );
    if (match) {
      setSelectedIds(new Set([match.id]));
      setDefaultChoice(match.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airlines]);

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Items eligible to be the default = selected existing airlines + the new one (if toggled).
  const defaultOptions: string[] = [
    ...selectable.filter((a) => selectedIds.has(a.id)).map((a) => a.id),
    ...(createNew && newName.trim() ? ['__new__'] : []),
  ];
  const effectiveDefault =
    defaultChoice && defaultOptions.includes(defaultChoice)
      ? defaultChoice
      : defaultOptions[0] ?? null;

  const canConfirm =
    (selectedIds.size > 0 || (createNew && newName.trim().length > 0)) && !!effectiveDefault;

  const submit = () =>
    onConfirm({
      airline_ids: [...selectedIds],
      create_new_airline_name: createNew && newName.trim() ? newName.trim() : undefined,
      default_airline_id:
        effectiveDefault && effectiveDefault !== '__new__' ? effectiveDefault : undefined,
      default_is_new: effectiveDefault === '__new__',
      override_airline_role: override,
    });

  const filtered = selectable.filter(
    (a) =>
      a.name.toLowerCase().includes(filter.toLowerCase()) ||
      a.code.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <Modal onClose={onClose} title="Approve and invite prospect">
      <div className="space-y-3 text-sm">
        <p>
          Create a Supabase Auth user for <strong>{prospect.email}</strong>, attach them to the
          airline(s) you select below, and email them an invite link.
        </p>
        <p className="text-xs text-steel">
          Prospect entered company: <strong>{prospect.airline_name}</strong> — their own entry. Only
          create it as a new airline if that's really intended.
        </p>

        {/* Airline picker — select one or more existing airlines + mark the default */}
        <div>
          <label className="block text-xs font-medium text-steel mb-1">
            Airlines — select one or more, and pick the primary (login lands there)
          </label>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter airlines…"
            className="w-full px-2 py-1.5 border border-steel/30 rounded text-sm mb-1"
          />
          <div className="max-h-44 overflow-y-auto border border-steel/20 rounded divide-y divide-steel/10">
            {airlinesLoading && <div className="px-2 py-2 text-steel">Loading airlines…</div>}
            {!airlinesLoading && filtered.length === 0 && (
              <div className="px-2 py-2 text-steel">No airlines match.</div>
            )}
            {filtered.map((a) => {
              const checked = selectedIds.has(a.id);
              return (
                <div key={a.id} className="flex items-center justify-between px-2 py-1.5">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input type="checkbox" checked={checked} onChange={() => toggle(a.id)} />
                    <span>{a.name}</span>
                    <span className="text-xs text-steel">{a.code}</span>
                  </label>
                  {checked && (
                    <label className="flex items-center gap-1 text-xs text-steel cursor-pointer">
                      <input
                        type="radio"
                        name="default-airline"
                        checked={effectiveDefault === a.id}
                        onChange={() => setDefaultChoice(a.id)}
                      />
                      primary
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Optionally create a brand-new airline */}
        <div className="border border-steel/20 rounded px-2 py-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={createNew}
              onChange={(e) => setCreateNew(e.target.checked)}
            />
            <span className="text-xs font-medium text-steel">Create a new airline</span>
          </label>
          {createNew && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New airline name"
                className="flex-1 px-2 py-1.5 border border-steel/30 rounded text-sm"
              />
              <label className="flex items-center gap-1 text-xs text-steel cursor-pointer whitespace-nowrap">
                <input
                  type="radio"
                  name="default-airline"
                  checked={effectiveDefault === '__new__'}
                  onChange={() => setDefaultChoice('__new__')}
                  disabled={!newName.trim()}
                />
                primary
              </label>
            </div>
          )}
        </div>

        {/* Role (applied to all selected airlines) */}
        <div>
          <label className="block text-xs font-medium text-steel mb-1">
            Airline role (applied to all selected)
          </label>
          <select
            value={override}
            onChange={(e) => setOverride(e.target.value as Prospect['airline_role'])}
            className="px-2 py-1.5 border border-steel/30 rounded text-sm"
          >
            <option value="admin">admin</option>
            <option value="editor">editor</option>
            <option value="viewer">viewer</option>
          </select>
          <p className="text-xs text-steel mt-1">
            Defaults to the role they requested ({prospect.airline_role}).
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-steel/30 rounded text-sm"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-orca-elevate text-white rounded text-sm font-medium hover:bg-orca-elevate/90 disabled:opacity-50"
            disabled={isPending || !canConfirm}
          >
            {isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {isPending ? 'Working…' : 'Approve & invite'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================
// Decline modal
// =====================================================================

function DeclineModal({
  prospect,
  onClose,
  onConfirm,
  isPending,
  error,
}: {
  prospect: Prospect;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
  error: string | null;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal onClose={onClose} title="Decline prospect">
      <div className="space-y-3 text-sm">
        <p>
          This marks <strong>{prospect.email}</strong> as declined. No email is sent in v1. You can
          re-open by setting status back to 'new' via SQL if needed.
        </p>
        <div>
          <label className="block text-xs font-medium text-steel mb-1">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="w-full px-2 py-1.5 border border-steel/30 rounded text-sm"
            placeholder="Stored in admin_notes for the audit trail"
          />
        </div>
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-steel/30 rounded text-sm"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            disabled={isPending}
          >
            {isPending ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
            {isPending ? 'Working…' : 'Decline'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================
// Modal shell
// =====================================================================

function Modal({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-steel hover:text-orca-command">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
